//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { Header, Segment, Table } from "semantic-ui-react";
import { Schedule, ScheduleChangeAudit } from "../../api/cd-robot/v1/cd_robot";
import { Field, FieldContent, FieldLabelWide } from "../../ui/lib";
import { FormattedDateWithLabel, DateTimeTransformationTypes } from "../../util/dateAndTimeUtils/DateTime";
import { actorLabel, hasScheduleChangeAudit, timestampToIso } from "./auditDisplay";
import { dailyUtcCronToLocalTime, formatUtcTimeLabel, isDailyUtcCron } from "./cronSchedule";

const formatScheduleCell = (schedule?: Schedule): string => {
  if (!schedule) {
    return "—";
  }
  const rows: string[] = [];
  const addRow = (label: string, cron?: string) => {
    if (!cron) {
      return;
    }
    if (isDailyUtcCron(cron)) {
      const local = dailyUtcCronToLocalTime(cron);
      rows.push(`${label}: ${local} local (${formatUtcTimeLabel(cron)} UTC)`);
    } else {
      rows.push(`${label}: ${cron}`);
    }
  };
  addRow("Start", schedule.start_schedule);
  addRow("Stop", schedule.stop_schedule);
  addRow("Stopped", schedule.stopped_schedule);
  if (schedule.preflight_lead_time) {
    rows.push(`Pre-flight: ${schedule.preflight_lead_time}`);
  }
  if (schedule.run_in_weekends) {
    rows.push("Weekends: yes");
  }
  return rows.length > 0 ? rows.join(" · ") : "—";
};

interface ScheduleChangeCardProps {
  change?: ScheduleChangeAudit;
}

export const ScheduleChangeCard = ({ change }: ScheduleChangeCardProps) => {
  if (!hasScheduleChangeAudit(change)) {
    return null;
  }

  const changedIso = timestampToIso(change?.changed_at);

  return (
    <Segment>
      <Header sub>Last schedule change</Header>
      <Field>
        <FieldLabelWide>Changed at</FieldLabelWide>
        <FieldContent>{changedIso ? <FormattedDateWithLabel dateTime={changedIso} transformTo={DateTimeTransformationTypes.LOCAL} /> : "—"}</FieldContent>
      </Field>
      <Field>
        <FieldLabelWide>Changed by</FieldLabelWide>
        <FieldContent>{actorLabel(change?.changed_by_id, change?.changed_by_email)}</FieldContent>
      </Field>
      <Table compact="very" celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell />
            <Table.HeaderCell>Previous</Table.HeaderCell>
            <Table.HeaderCell>New</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Schedule</Table.Cell>
            <Table.Cell>{formatScheduleCell(change?.previous_schedule)}</Table.Cell>
            <Table.Cell>{formatScheduleCell(change?.new_schedule)}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </Segment>
  );
};
